import ROUTES from "../router/routesModel";

/**
 * B-43. Trade emails link to the website (server/notifications/notificationEmail.js
 * `linkFor`: FRONTEND_URL + /trades/<id> or /chat?threadId=<id>). The Android app
 * claimed no https link, so tapping one opened the browser, where the collector
 * is not signed in. The manifest now declares a verified App Link for this host
 * and these paths, Android hands the link to the running app as Capacitor's
 * `appUrlOpen`, and this turns it into the in-app route.
 *
 * Must match, all at once:
 *   - android:host on the autoVerify intent filter in AndroidManifest.xml,
 *   - FRONTEND_URL in server/config/production.json (the host emails link to),
 *   - the site that serves /.well-known/assetlinks.json (react-app/public).
 * src/lib/androidManifest.activity.test.js fails if the first two drift.
 *
 * Sign-in deliberately stays on the custom scheme (nativeAuth.js explains why):
 * if App Link verification ever fails, Android just opens these links in the
 * browser as before, which costs nothing. A sign-in redirect that fails
 * verification would break sign-in.
 */
export const APP_LINK_HOST = "shakethat-pika.onrender.com";

/**
 * Public trade lists (docs/PUBLIC_TRADE_LIST.md) are shared on the brand
 * domain: /t/<slug> is the link a collector posts, /trade-list/<slug>?card=<id>
 * is where its "Offer a trade" buttons go. Must match the second autoVerify
 * filter in AndroidManifest.xml.
 */
export const SHARE_LINK_HOST = "tradeloft.app";

/** Trade and chat-thread ids are Mongo ObjectIds; anything else is not ours. */
const ID = /^[A-Za-z0-9]{1,64}$/;
/** A trade-list slug (server/publicTradeList/slug.js). */
const SLUG = /^[A-Za-z0-9]{12}$/;
/** A catalog card id. */
const CARD_ID = /^[a-f0-9]{24}$/i;

/** The in-app route for a public trade-list link, or null. */
const parseShareLinkPath = (segments, searchParams) => {
  if (segments.length !== 3 || !SLUG.test(segments[2])) return null;
  if (segments[1] === "t") return `/t/${segments[2]}`;
  if (segments[1] === "trade-list") {
    const card = searchParams.get("card");
    return card && CARD_ID.test(card)
      ? `/trade-list/${segments[2]}?card=${card}`
      : `/trade-list/${segments[2]}`;
  }
  return null;
};

/**
 * The in-app route for an incoming https link, or null if it is not one the app
 * accepts.
 *
 * An allowlist, like parseDeepLinkAuthTarget: the activity is exported, so any
 * installed app can send it a URL, and a link must not steer the app anywhere
 * the emails do not already go.
 */
export const parseAppLinkTarget = (url) => {
  if (typeof url !== "string") return null;

  let parsed;
  try {
    parsed = new URL(url);
  } catch {
    return null;
  }
  if (parsed.protocol !== "https:") return null;
  if (parsed.port || parsed.username || parsed.password) return null;

  const path = parsed.pathname.replace(/\/+$/, "");
  const segments = path.split("/");

  if (parsed.hostname === SHARE_LINK_HOST) return parseShareLinkPath(segments, parsed.searchParams);
  if (parsed.hostname !== APP_LINK_HOST) return null;

  // "/trades/<id>"
  if (segments.length === 3 && `/${segments[1]}` === ROUTES.TRADES && ID.test(segments[2])) {
    return `${ROUTES.TRADES}/${segments[2]}`;
  }

  // "/chat" or "/chat?threadId=<id>"
  if (path === ROUTES.CHAT) {
    const threadId = parsed.searchParams.get("threadId");
    return threadId && ID.test(threadId) ? `${ROUTES.CHAT}?threadId=${threadId}` : ROUTES.CHAT;
  }

  return null;
};
